import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, LabelList } from "recharts";
import { useProfileStore } from "../../store/useUserStore";
import api from "../../api/api";
import { IoIosArrowBack } from "react-icons/io";

const RewardsView = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const { academyId, studentId } = useParams();
    const { startProgress, tickProgress } = useProfileStore((state) => state)
    const [rewardData, setRewardData] = useState<any>({})
    const [academyName, setAcademyName] = useState<string>("");
    const [years, setYears] = useState<string[]>([]);

    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const skillsData = Object.entries(rewardData?.skill_analysis || {}).map(([name, data]: any) => ({
        name,
        value: data?.count || 0,
    }));
    const colors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    const RewardSummaryCard = ({ title, value }: { title: string; value: number }) => (
        <div className="flex-1 rounded-xl bg-white p-4 shadow">
            <div className="text-center">
                <div className="text-[16px] font-medium text-black">{title}</div>
                <div className="flex justify-center gap-1.5 mt-1.5">
                    <span role="img" aria-label="star" className="text-[#FFC107] text-[20px]">★</span>
                    <div className="text-[16px] font-bold text-black">{value}</div>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        if (studentId && academyId) {
            setAcademyName(location.state?.academyName || "");
            getStudentReward();
        }
    }, [studentId, academyId]);

    function getStudentReward() {
        startProgress(1)
        api.get(`${process.env.REACT_APP_API_BASE_URL}/reward/get_reward_stats/${studentId}?academy_id=${academyId}`).then(res => {
            console.log("res reward", res.data.data)
            setRewardData(res.data.data)

            const resYears: string[] = []
            res.data.data?.point_history?.forEach(e => {
                const parsedDate = new Date(e.created_at)
                const year = parsedDate.getFullYear().toString()
                if (!resYears.includes(year)) {
                    resYears.push(year)
                }
            })
            setYears(resYears)
            setSelectedYear(resYears[0] || null)

        }).catch(err => {
            console.log("err", err)
        }).finally(() => {
            tickProgress()
        })
    }

    const RewardListItem = ({
        type,
        points,
        date,
        skillName,
        coachName,
    }: {
        type?: "rewardedSummary" | "history";
        points: number;
        date?: string;
        skillName?: string;
        coachName?: string;
    }) => (
        <div className="flex justify-between py-2 border-b border-gray-200">
            <div className="flex flex-col gap-1">
                {coachName && <div className="text-sm font-semibold text-black">{coachName}</div>}
                {skillName && <div className="text-xs text-gray-600">{skillName}</div>}
                {date && <div className="text-xs text-gray-500">{date}</div>}
            </div>
            <div className="text-sm font-bold text-black">{points}</div>
        </div>
    );

    return (
        <div className="relative min-h-screen text-white">
            <div className="absolute inset-0 bg-[#0d47a1] overflow-hidden">
                <div className="absolute rounded-full bg-[#69f0ae] top-[132px] right-[-32px] w-[320px] h-[320px]" />
                <div className="absolute rounded-full bg-[#69f0ae] top-[440px] left-[-16px] w-[240px] h-[240px]" />
                <div className="absolute rounded-full bg-[#69f0ae] top-[620px] right-[-32px] w-[120px] h-[120px]" />
            </div>
            <div className="relative z-10 p-4">
                <div className="flex items-center gap-2">
                    <IoIosArrowBack size={28} color='#fff' onClick={() => navigate('/profile')} />
                    <div className="w-10 h-10 rounded-full bg-[#1976d2]" />
                    <div className="text-[16px] font-bold ml-2">{academyName}</div>
                </div>
                <div className="my-4 text-[32px] font-bold">{rewardData?.student_name}</div>
                <div className="rounded-xl bg-gray-800 text-white flex items-center justify-between p-4">
                    <div className="text-[16px] font-bold">Year</div>
                    <select
                        value={selectedYear || ""}
                        onChange={(e) => setSelectedYear(e.target.value || null)}
                        className="px-4 py-2 rounded-lg border border-gray-200 min-w-40 bg-white text-black"
                    >
                        <option value="">{selectedYear || "All Years"}</option>
                        {years.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="my-4">
                    <div className="rounded-xl bg-white p-4 text-center shadow">
                        <div className="text-[16px] font-bold text-black">Current Star Balance</div>
                        <div className="flex justify-center items-center gap-2 mt-2">
                            <span role="img" aria-label="star" className="text-[#FFC107] text-[32px]">★</span>
                            <div className="text-[24px] font-bold text-black">{rewardData?.summary?.current_balance || 0}</div>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <RewardSummaryCard title="Total Reward Star" value={rewardData?.summary?.total_earned || 0} />
                    <RewardSummaryCard title="Redeemed Stars" value={rewardData?.summary?.total_spent || 0} />
                </div>
                <div className="my-4">
                    <div className="rounded-xl bg-white p-4 shadow text-center">
                        <div className="text-[16px] font-normal text-blue-600">✨ Your Best Skill</div>
                        <div className="text-[20px] font-bold mt-1.5 text-black">{rewardData?.summary?.best_skill || ""}</div>
                    </div>
                </div>
                <div className="my-4">
                    <div className="rounded-xl bg-white p-4 shadow text-center">
                        <div className="text-[16px] font-semibold mb-1.5 text-black">🚀 Your Skill Ratio</div>
                        <div className="h-64">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={skillsData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                                        {skillsData.map((entry, idx) => (
                                            <Cell key={idx} fill={colors[idx % colors.length]} />
                                        ))}
                                        <LabelList dataKey="name" position="outside" />
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="my-4">
                    <div className="rounded-xl bg-white p-4 shadow">
                        <div className="flex items-center justify-between py-2">
                            <div className="text-[18px] font-semibold text-black">Rewarded by:</div>
                        </div>
                        <div className="border-t border-gray-200 my-2" />
                        {rewardData?.coach_breakdown?.map((e, idx) => (
                            <RewardListItem key={`${e.coach_name}-${idx}`} points={e.total_given || 0} coachName={e.coach_name} />
                        ))}
                    </div>
                </div>
                <div className="my-4">
                    <div className="rounded-xl bg-white p-4 shadow">
                        <div className="text-[18px] font-semibold py-2 text-black">Points History:</div>
                        <div className="border-t border-gray-200 my-2" />
                        {rewardData?.point_history?.map((e, idx) => (
                            <RewardListItem
                                key={`${e.created_at}-${idx}`}
                                type="history"
                                points={e.points}
                                date={e.created_at}
                                skillName={e.skill_name}
                                coachName={e.coach_name}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RewardsView;
